/*
  EME Studios accordion behaviour, ported from _qa/eme/js/accordion-YMk7zpGW.js
  (Radix Collapsible + Radix Accordion, vertical orientation).

  What is ported:
    - button + aria-expanded + aria-controls, region labelled by the trigger
    - data-state="open"|"closed" mirrored onto item / header / trigger / content,
      which is what every selector in eme-accordion.css keys off
    - Radix Accordion.Root's roving keyboard nav: Home / End / ArrowDown / ArrowUp
    - type="single": opening one item closes its siblings. Opt in with data-group.
      EME uses this for both of its own accordions (the mobile footer and the PDP
      info tabs both mount <Accordion type="single">).

  Deliberately not ported:
    - horizontal orientation and its ArrowLeft/ArrowRight handling. No EME consumer
      uses it, and `orientation` is never passed anywhere in their bundle.
    - the disabled state. Radix supports it, no EME consumer sets it.
    - the JS height measurement. CSS does the same animation, see eme-accordion.css.
*/
if (!customElements.get('eme-accordion')) {
  customElements.define(
    'eme-accordion',
    class EmeAccordion extends HTMLElement {
      /* EME's animation duration. Keep in step with the .2s in eme-accordion.css. */
      static DURATION = 200;

      connectedCallback() {
        this.trigger = this.querySelector('.eme-accordion__trigger');
        this.content = this.querySelector('.eme-accordion__content');
        if (!this.trigger || this.trigger.dataset.emeBound) return;
        this.trigger.dataset.emeBound = 'true';
        this.trigger.addEventListener('click', () => this.toggle());
        this.trigger.addEventListener('keydown', (event) => this.handleKeydown(event));
        if (this.content) {
          this.content.addEventListener('animationend', () => this.finishClose());
        }
      }

      /*
        The panel only becomes display:none once it reaches `closed`, so this MUST run even
        if animationend never arrives -- otherwise a missed event leaves the panel expanded
        for good. animationend does not fire in a backgrounded tab, and it does not fire at
        all when the animation is cancelled or suppressed (reduced motion, print), so the
        timer is the guarantee and the event is only there to finish early.
      */
      finishClose() {
        clearTimeout(this.closeTimer);
        if (this.content && this.content.dataset.state === 'closing') {
          this.content.dataset.state = 'closed';
        }
      }

      get open() {
        return this.dataset.state === 'open';
      }

      /*
        Height is measured and handed to the keyframes as --eme-accordion-content-height,
        exactly as Radix hands its own measurement to --radix-accordion-content-height.
        Measuring per toggle (rather than pinning a height) leaves the panel at height:auto
        at rest, so it stays correct when the text rewraps at another viewport width.
      */
      set open(value) {
        /*
          Set synchronously, in the same style resolution as the state change, so the
          animation rule starts matching exactly when the first toggle happens. That
          suppresses the expand on page load for a server-rendered open item (Radix's
          first-frame guard) without depending on requestAnimationFrame, which never runs
          while the tab is backgrounded.
        */
        this.dataset.emeReady = '';
        const state = value ? 'open' : 'closed';
        this.dataset.state = state;
        if (this.trigger) {
          this.trigger.dataset.state = state;
          this.trigger.setAttribute('aria-expanded', String(!!value));
        }
        const header = this.querySelector('.eme-accordion__header');
        if (header) header.dataset.state = state;
        if (!this.content) return;

        if (value) {
          /* Suppress the keyframes while we display the panel to measure it, then release. */
          this.content.style.animation = 'none';
          this.content.dataset.state = 'open';
          this.setHeightVar();
          void this.content.offsetHeight;
          this.content.style.animation = '';
        } else {
          this.setHeightVar();
          this.content.dataset.state = 'closing';
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => this.finishClose(), EmeAccordion.DURATION);
        }
      }

      setHeightVar() {
        this.content.style.setProperty(
          '--eme-accordion-content-height',
          this.content.scrollHeight + 'px'
        );
      }

      toggle() {
        const next = !this.open;
        if (next) this.closeSiblings();
        this.open = next;
      }

      /* Radix type="single": only one item in a group is open at a time. */
      closeSiblings() {
        const group = this.dataset.group;
        if (!group) return;
        this.siblings(group).forEach((el) => {
          if (el !== this && el.open) el.open = false;
        });
      }

      siblings(group) {
        const all = Array.from(document.querySelectorAll('eme-accordion[data-group]'));
        return all.filter((el) => el.dataset.group === group);
      }

      handleKeydown(event) {
        const keys = ['Home', 'End', 'ArrowDown', 'ArrowUp'];
        if (!keys.includes(event.key)) return;

        const scope = this.dataset.group
          ? this.siblings(this.dataset.group)
          : Array.from(this.parentElement ? this.parentElement.children : []).filter(
              (el) => el.tagName === 'EME-ACCORDION'
            );
        const triggers = scope
          .map((el) => el.querySelector('.eme-accordion__trigger'))
          .filter((t) => t && !t.disabled);

        const index = triggers.indexOf(event.target);
        if (index === -1) return;
        event.preventDefault();

        const last = triggers.length - 1;
        let next = index;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;
        else if (event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
        else next = index === 0 ? last : index - 1;

        triggers[next].focus();
      }
    }
  );
}
